import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  padding: 20px;
`;

const Message = styled.div`
  color: var(--color-text);
  font-size: 18px;
  margin-bottom: 20px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  font-size: 16px;
  margin-top: 20px;
  padding: 12px 20px;
  background: var(--color-error-light, rgba(239, 68, 68, 0.1));
  border: 1px solid var(--color-error-border, rgba(239, 68, 68, 0.2));
  border-radius: var(--radius-md);
  max-width: 400px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  border: 3px solid var(--color-surface);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('AuthCallback: Processing authentication...');
        console.log('Current URL:', window.location.href);
        console.log('Hash:', window.location.hash);
        console.log('Search:', window.location.search);

        // Get the session from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        // Check for error in URL parameters
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
        
        if (error) {
          console.error('Auth error from URL:', error, errorDescription);
          setError(errorDescription || error);
          return;
        }

        // Wait a bit for Supabase to process the auth
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if we have a session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw sessionError;
        }

        if (session) {
          console.log('Session found:', session.user.email);
          // Successfully authenticated, redirect to home
          navigate('/', { replace: true });
        } else if (retryCount < 3) {
          // Retry a few times for slow connections
          console.log(`No session yet, retrying... (attempt ${retryCount + 1})`);
          setRetryCount(retryCount + 1);
          setTimeout(() => handleCallback(), 1500);
        } else {
          // After retries, still no session
          console.error('No session found after retries');
          setError('Authentication failed. Please try again.');
        }
      } catch (err: any) {
        console.error('Auth callback error:', err);
        setError(err.message || 'An error occurred during authentication');
      }
    };

    handleCallback();
  }, [navigate, retryCount]);

  if (error) {
    return (
      <Container>
        <Message>Authentication Error</Message>
        <ErrorMessage>{error}</ErrorMessage>
        <button 
          onClick={() => navigate('/')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Back to Login
        </button>
      </Container>
    );
  }

  return (
    <Container>
      <LoadingSpinner />
      <Message>Completing sign in...</Message>
    </Container>
  );
};